import { useLocation } from 'react-router-dom';
import { PathnameEnum } from '../../../../../constants';
import {
  FilterKeys,
  ignoredFilters,
  labelMap,
  MovementStatusEnum,
} from '../../../constants';
import { formatValue } from './formatValue';

export const generateFilters = (
  params: URLSearchParams,
  loadCompanyFilterListMap: any[],
) => {
  const filters: { key: string; label: string; value: string }[] = [];
  const processedKeys = new Set<string>();
  const { pathname } = useLocation();

  const addFilter = (key: string, label: string, value: string) => {
    if (!processedKeys.has(key)) {
      filters.push({ key, label, value });
      processedKeys.add(key);
    }
  };

  const organizationalGroupParams = params.getAll(
    FilterKeys.ORGANIZATIONAL_GROUP,
  );

  const organizations = organizationalGroupParams.flatMap(item =>
    item.split(','),
  );

  if (organizations.length > 0) {
    const label =
      organizations.length > 1
        ? `${organizations.length} empresas selecionadas`
        : loadCompanyFilterListMap.find(
            (org: any) => org.id === organizations[0],
          )?.name.trade || `${organizations.length} empresa selecionada`;

    const value = organizations
      .map(id => `${FilterKeys.ORGANIZATIONAL_GROUP}=${id}`)
      .join('&');

    addFilter(FilterKeys.ORGANIZATIONAL_GROUP, label, value);
  }

  // Iteração sobre os parâmetros restantes
  Array.from(params).forEach(([key, value]) => {
    const filterKey = key as FilterKeys;

    if (ignoredFilters.includes(key) || processedKeys.has(key)) {
      return;
    }

    if (labelMap.hasOwnProperty(filterKey)) {
      const labelMapKey = labelMap[filterKey];

      if (filterKey === FilterKeys.BENEFICIARIES_TYPE) {
        const beneficiaries = value.split(',');
        const label =
          beneficiaries.length > 1
            ? `${beneficiaries.length} tipos de beneficiários selecionados`
            : labelMapKey[beneficiaries[0] as keyof typeof labelMapKey];

        addFilter(
          filterKey,
          label,
          beneficiaries.map(id => `${filterKey}=${id}`).join('&'),
        );
        return;
      }

      if (filterKey === FilterKeys.STATUS) {
        const statusParams = value.split(',');
        const isMovementStatus = statusParams.every(param =>
          Object.values(MovementStatusEnum).includes(
            param as MovementStatusEnum,
          ),
        );

        const isUserStatus =
          pathname.includes(PathnameEnum.BACKOFFICE_USERS) ||
          pathname.includes(PathnameEnum.USER);

        const newLabelMap = isUserStatus
          ? labelMap[FilterKeys.USER_STATUS]
          : labelMap[FilterKeys.MOVEMENT_STATUS];

        if (isMovementStatus || isUserStatus) {
          const label =
            statusParams.length > 1
              ? `${statusParams.length} status selecionados`
              : newLabelMap[statusParams[0] as keyof typeof labelMapKey];

          addFilter(
            filterKey,
            label,
            statusParams.map(id => `${filterKey}=${id}`).join('&'),
          );
          return;
        }
      }

      if (filterKey === FilterKeys.PLAN_TYPE) {
        const plans = value.split(',');
        const label =
          plans.length > 1
            ? `${plans.length} tipos de planos selecionados`
            : labelMapKey[plans[0] as keyof typeof labelMapKey];

        addFilter(
          filterKey,
          label,
          plans.map(id => `${filterKey}=${id}`).join('&'),
        );
        return;
      }

      if (filterKey === FilterKeys.OPERATION_STATUS) {
        const status = value.split(',');
        const label =
          status.length > 1
            ? `${status.length} tipos de status selecionados`
            : labelMapKey[status[0] as keyof typeof labelMapKey];

        addFilter(
          filterKey,
          label,
          status.map(id => `${filterKey}=${id}`).join('&'),
        );
        return;
      }

      if (filterKey === FilterKeys.CONTRACT_TYPE) {
        const contracts = value.split(',');
        const label =
          contracts.length > 1
            ? `${contracts.length} tipos de contratos selecionados`
            : labelMapKey[contracts[0] as keyof typeof labelMapKey];

        addFilter(filterKey, label ?? value, `${filterKey}=${value}`);
        return;
      }

      if (filterKey === FilterKeys.PROFILE) {
        const contracts = value.split(',');
        const label =
          contracts.length > 1
            ? `${contracts.length} perfis selecionados`
            : labelMapKey[contracts[0] as keyof typeof labelMapKey];

        addFilter(filterKey, label ?? value, `${filterKey}=${value}`);
        return;
      }

      if (
        filterKey === FilterKeys.STATUS &&
        pathname.includes(PathnameEnum.COMPANY)
      ) {
        const label = labelMapKey[value as keyof typeof labelMapKey];

        addFilter(filterKey, label ?? value, `${filterKey}=${value}`);
        return;
      }
    }

    if (
      filterKey === FilterKeys.START_DATE &&
      params.has(FilterKeys.END_DATE)
    ) {
      const startDate = formatValue(FilterKeys.START_DATE, value);
      const endDate = formatValue(
        FilterKeys.END_DATE,
        params.get(FilterKeys.END_DATE)!,
      );

      addFilter(
        'start-end-date' as FilterKeys,
        `${labelMap[FilterKeys.START_DATE]} (${startDate}) - ${
          labelMap[FilterKeys.END_DATE]
        } (${endDate})`,
        `${FilterKeys.START_DATE}=${value}&${FilterKeys.END_DATE}=${params.get(
          FilterKeys.END_DATE,
        )}`,
      );
      return;
    }

    addFilter(
      filterKey,
      `${labelMap[filterKey] ?? value}`,
      `${filterKey}=${value}`,
    );
  });

  return filters;
};
